import { APP_CRITICALITIES, Configuration } from "vmd-types";
import { getReviewStageName, type ReviewStage } from "vmd-utils";

export const VMD_COLORS = {
  green: "#4caf50",
  red: "#f44336",
  grey: "#424242",
  yellow: "#f9a825",
  babyBlue: "#94CADC",
  cyanBlue: "#2196F3",
} as const;

export const DONUT_CHART_COLORS = [
  "#0B2753",
  "#1D4ED8",
  "#0891B2",
  "#059669",
  "#D97706",
  "#DC2626",
  "#7C3AED",
  "#475569",
];

export const CRITICALITY_COLORS_BY_KEY: Record<
  keyof typeof APP_CRITICALITIES,
  string
> = {
  VERY_LOW: "#8BC34A",
  LOW: "#4CAF7D",
  MEDIUM: "#FFC107",
  HIGH: "#FF7043",
  VERY_HIGH: "#C62828",
  NOT_APPLICABLE: VMD_COLORS.grey,
};

// Criticality badges/chart labels always use white text, regardless of the
// background shade (see gitlab.com/hipsquare/pimo/-/work_items/2611).
export const CRITICALITY_TEXT_COLOR = "#fff";

// Review Status badge tints. Pale backgrounds with a dark label, so the only
// saturated chip left in a row is Criticality.
export const REVIEW_STATUS_BADGE_COLORS = {
  submitted: { background: "#E5F0EB", text: "#467C69" },
  readyForSubmission: { background: "#FFF4DC", text: "#946C1C" },
  inProgress: { background: "#E6F0FA", text: "#35658F" },
  notStarted: { background: "#F0F2F5", text: "#6B7280" },
  unknown: { background: "#F8FAFC", text: "#64748B" },
} as const;

// Resolved newest-stage-first: `getReviewStageName` maps "submitted" to the
// last definition whatever the stage count, so a short configuration must
// match it before the earlier stages claim the same entry.
const REVIEW_STAGES_BY_PRECEDENCE: readonly ReviewStage[] = [
  "submitted",
  "readyForSubmission",
  "inProgress",
  "notStarted",
];

/**
 * Review status names are admin-editable, so the tint is resolved through the
 * configuration's stage order rather than by comparing against a literal like
 * "Submitted". A renamed or unrecognized status falls back to the neutral tint.
 */
export const getReviewStatusBadgeColors = (
  status: string | undefined,
  configuration: Pick<Configuration, "degreeOfCompletionDefinition"> | undefined
) => {
  if (!status) {
    return REVIEW_STATUS_BADGE_COLORS.unknown;
  }

  const stage = REVIEW_STAGES_BY_PRECEDENCE.find(
    (candidate) => getReviewStageName(configuration, candidate) === status
  );

  return stage
    ? REVIEW_STATUS_BADGE_COLORS[stage]
    : REVIEW_STATUS_BADGE_COLORS.unknown;
};

/** `BadgeCell` props for a review status, resolved through the configured stages. */
export const getReviewStatusBadgeProps = (
  status: string | undefined,
  configuration: Pick<Configuration, "degreeOfCompletionDefinition"> | undefined
) => {
  const colors = getReviewStatusBadgeColors(status, configuration);
  return { color: colors.background, textColor: colors.text };
};

// DC Relevance is a dot rather than a chip, so it drops out of the row's color
// competition entirely: filled for Yes, a hollow ring for No, grey for unreviewed.
export const DC_RELEVANCE_INDICATOR_COLOR = "#5D88BE";
export const DC_RELEVANCE_UNREVIEWED_COLOR = "#B8C2CC";

export type DcRelevanceIndicator = {
  color: string;
  filled: boolean;
};

export const getDcRelevanceIndicator = (
  value: string | undefined
): DcRelevanceIndicator =>
  value === "Yes"
    ? { color: DC_RELEVANCE_INDICATOR_COLOR, filled: true }
    : value === "No"
      ? { color: DC_RELEVANCE_INDICATOR_COLOR, filled: false }
      : { color: DC_RELEVANCE_UNREVIEWED_COLOR, filled: false };

// Background tints for the "record type" badge shown on Business Apps / IT
// Services (overview table, dashboard channels card, report custom fields).
export const RECORD_TYPE_BADGE_COLORS = {
  businessApplication: "#DFEFF2",
  itService: "#E1F1E7",
  fallback: "#E2E8F0",
} as const;

// Text/background pair for the "active"/"constantly active" success chip
// shown across manage-users and the data-model field editors.
export const SUCCESS_COLORS = {
  text: "#008336",
  background: "#e6f9e6",
} as const;

// Green used for the "active" state of the custom AntSwitch toggle.
export const TOGGLE_GREEN = "#6fe255";

// Background for the purple "use case" / "invitation pending" badges.
export const PURPLE_BADGE_BACKGROUND = "#EBE3F4";

// Default swatch color for a newly added custom-field select option.
export const CATEGORY_OPTION_DEFAULT_COLOR = "#ccc";

// Accent blue used for progress bars/charts in the report and dashboard-audit
// pages.
export const CHART_ACCENT_BLUE = "#38BDF8";

// Secondary/caption text grey, matches theme.palette.primary.contrastText;
// used in plain (non-component) config/helper files without theme access.
export const SECONDARY_TEXT_COLOR = "#414141";

// Fallback grey for a Badge when no color can be resolved for its value.
export const BADGE_FALLBACK_COLOR = "#9e9e9e";

// Background/border/text triplet per risk level for the STRIDE findings risk
// heat map (see gitlab.com/hipsquare/pimo/-/work_items/2413).
export const RISK_HEAT_MAP_COLORS = {
  high: { background: "#EFC3C2", border: "#D98F8C", text: "#812A2A" },
  medium: { background: "#F2C981", border: "#EAC98E", text: "#673B06" },
  low: { background: "#C6DC9E", border: "#C7DDAC", text: "#28520C" },
} as const;

// Single base blue hue for the "business apps per distribution channel and
// OE" heat map (#2608), derived from reference color #D3E3EF
// (hsl(206, 47%, 88%)); every band is a shade of this one hue/saturation,
// darker for higher business-app counts — see
// `getBusinessAppCountColorScaleRanges`.
export const DISTRIBUTION_CHANNEL_OE_MATRIX_HUE = 206;
export const DISTRIBUTION_CHANNEL_OE_MATRIX_SATURATION = 47;

export const getDistributionChannelOeMatrixShade = (lightness: number) =>
  `hsl(${DISTRIBUTION_CHANNEL_OE_MATRIX_HUE}, ${DISTRIBUTION_CHANNEL_OE_MATRIX_SATURATION}%, ${lightness}%)`;

// Contrast text colors for the "business apps per distribution channel and
// OE" heat map (#2608): text flips between these depending on how dark the
// cell's background shade is, see `getBusinessAppCountColorScaleRanges`.
export const DISTRIBUTION_CHANNEL_OE_MATRIX_DARK_TEXT = "#0F2530";
export const DISTRIBUTION_CHANNEL_OE_MATRIX_LIGHT_TEXT = "#FFFFFF";
